/**
 * Загрузка больших файлов.
 *
 * Файл на 10 ГБ нельзя отправить одним запросом: браузер держит его в
 * памяти, любой обрыв обнуляет прогресс, а сервер становится узким местом.
 *
 * Как работает здесь:
 *   1. Спрашиваем у API размер части и их количество
 *   2. Режем файл срезами — браузер читает их лениво, память не растёт
 *   3. Просим подписанные ссылки пачками
 *   4. Грузим части НАПРЯМУЮ в хранилище, по несколько параллельно
 *   5. Оборвалось — повторяем только упавшую часть, не весь файл
 *   6. В конце собираем и сверяем размер
 *
 * Отправка идёт через XMLHttpRequest, а не через современный fetch.
 * Причина одна: fetch не умеет сообщать, сколько байт уже ушло — он
 * говорит только «начал» и «закончил». С ним прогресс двигался бы
 * рывками по целому куску, то есть скачками в несколько процентов.
 * XMLHttpRequest докладывает о каждом килобайте, и полоска идёт плавно.
 */
import { api } from './api';

export type UploadProgress = {
  /** 0–100 */
  percent: number;
  uploadedBytes: number;
  totalBytes: number;
  /** МБ/с, сглаженная */
  speedMbps: number;
  /** Секунд до конца, null пока не с чего считать */
  etaSec: number | null;
  status: 'preparing' | 'uploading' | 'finishing' | 'done' | 'error';
  error?: string;
};

type InitResponse = {
  fileId: string;
  chunkSize: number;
  totalParts: number;
  batchSize: number;
  parallel?: number;
};

type PartUrl = { partNumber: number; url: string };

const DEFAULT_PARALLEL = 6;
const MAX_RETRIES = 5;

/** Чаще этого обновлять полоску незачем — глаз всё равно не различит */
const EMIT_INTERVAL_MS = 100;

/**
 * Окно для расчёта скорости. Пять секунд — компромисс: короче даёт
 * дёрганые цифры от каждого сетевого чиха, длиннее слишком медленно
 * реагирует на реальное изменение канала.
 */
const SPEED_WINDOW_MS = 5000;

export class Uploader {
  private aborted = false;
  private requests = new Set<XMLHttpRequest>();

  /** Байты полностью загруженных частей */
  private completedBytes = 0;
  /** Байты частей, которые летят прямо сейчас: номер части → отправлено */
  private inflight = new Map<number, number>();

  private lastEmit = 0;

  /**
   * Замеры для расчёта скорости: время и сколько байт ушло к этому моменту.
   *
   * Считать среднюю за всё время нельзя. В первые секунды байты мгновенно
   * уходят в буферы системы и сети, и скорость выглядит завышенной. Потом
   * средняя медленно сползает к правде, и человеку кажется, что соединение
   * деградирует — хотя оно с самого начала было таким.
   *
   * Поэтому считаем по скользящему окну: сколько ушло за последние секунды.
   */
  private samples: Array<{ at: number; bytes: number }> = [];

  constructor(
    private file: File,
    private videoId: string,
    private kind: 'MATERIAL' | 'FINAL' | 'COVER',
    private onProgress: (p: UploadProgress) => void,
  ) {}

  /** Прервать загрузку */
  abort() {
    this.aborted = true;
    for (const xhr of this.requests) xhr.abort();
  }

  async start(): Promise<string> {
    this.samples.push({ at: Date.now(), bytes: 0 });
    this.emit('preparing', undefined, true);

    let init: InitResponse;
    try {
      init = await api.post<InitResponse>('/api/files/upload/init', {
        videoId: this.videoId,
        kind: this.kind,
        filename: this.file.name,
        sizeBytes: this.file.size,
        mimeType: this.file.type || undefined,
      });
    } catch (err) {
      this.emit('error', err instanceof Error ? err.message : 'Не удалось начать загрузку', true);
      throw err;
    }

    const { fileId, chunkSize, totalParts, batchSize } = init;
    const parallel = init.parallel ?? DEFAULT_PARALLEL;
    const done = new Map<number, string>(); // номер части → ETag

    try {
      // Идём пачками: просить ссылки сразу на тысячу частей бессмысленно,
      // они успеют протухнуть до того, как до них дойдёт очередь
      for (let from = 1; from <= totalParts; from += batchSize) {
        if (this.aborted) throw new Error('Загрузка отменена');

        const numbers: number[] = [];
        for (let n = from; n < from + batchSize && n <= totalParts; n++) {
          if (!done.has(n)) numbers.push(n);
        }
        if (numbers.length === 0) continue;

        const urls = await this.retrying(() =>
          api.post<PartUrl[]>(`/api/files/${fileId}/parts`, { partNumbers: numbers }),
        );

        await this.uploadBatch(urls, chunkSize, done, parallel);
      }

      this.emit('finishing', undefined, true);

      await this.retrying(() =>
        api.post(`/api/files/${fileId}/complete`, {
          parts: [...done.entries()].map(([partNumber, etag]) => ({ partNumber, etag })),
        }),
      );

      this.emit('done', undefined, true);
      return fileId;
    } catch (err) {
      // Прибираем за собой: незавершённая загрузка иначе продолжит
      // занимать место в хранилище
      await api.post(`/api/files/${fileId}/abort`).catch(() => {});
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      this.emit('error', message, true);
      throw err;
    }
  }

  /**
   * Повтор запроса к API с нарастающей паузой.
   *
   * Сами куски летят мимо API прямо в хранилище и обрыв переживают —
   * у них свои пять попыток. А вот запросы к серверу за новой пачкой
   * ссылок и за завершением сборки повторов не имели: секундная
   * недоступность API — и многочасовая заливка обнулялась целиком,
   * причём в самом обидном месте, у отметки «почти готово».
   *
   * Оба запроса безопасно повторять: ссылки выдаются заново, а сборка
   * файла при повторном вызове просто возвращает уже собранное.
   */
  private async retrying<T>(fn: () => Promise<T>, attempts = 5): Promise<T> {
    let lastError: unknown;

    for (let attempt = 0; attempt < attempts; attempt++) {
      if (this.aborted) throw new Error('Загрузка отменена');
      try {
        return await fn();
      } catch (err) {
        lastError = err;
        if (this.aborted) throw err;
        await sleep(1000 * 2 ** attempt);
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error('Сервер не отвечает. Попробуй позже.');
  }

  /** Пачка частей, по несколько параллельно */
  private async uploadBatch(
    urls: PartUrl[],
    chunkSize: number,
    done: Map<number, string>,
    parallel: number,
  ) {
    const queue = [...urls];

    const worker = async () => {
      while (queue.length > 0) {
        if (this.aborted) throw new Error('Загрузка отменена');
        const part = queue.shift();
        if (!part) return;

        const start = (part.partNumber - 1) * chunkSize;
        const blob = this.file.slice(start, Math.min(start + chunkSize, this.file.size));

        const etag = await this.uploadPart(part.url, blob, part.partNumber);
        done.set(part.partNumber, etag);

        // Часть долетела: переносим её байты из «в полёте» в «готово»
        this.inflight.delete(part.partNumber);
        this.completedBytes += blob.size;
        this.emit('uploading', undefined, true);
      }
    };

    await Promise.all(Array.from({ length: Math.min(parallel, urls.length) }, worker));
  }

  /**
   * Одна часть с повторами.
   *
   * Пауза растёт: 1с, 2с, 4с... Обрыв связи обычно длится несколько секунд,
   * и агрессивные повторы только мешают восстановлению.
   */
  private async uploadPart(url: string, blob: Blob, partNumber: number): Promise<string> {
    let lastError: unknown;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (this.aborted) throw new Error('Загрузка отменена');

      try {
        return await this.sendPart(url, blob, partNumber);
      } catch (err) {
        lastError = err;
        if (this.aborted) throw err;
        // Неудачная попытка: то, что успело уйти, не считается
        this.inflight.set(partNumber, 0);
        this.emit('uploading', undefined, true);
        await sleep(1000 * 2 ** attempt);
      }
    }

    throw new Error(
      `Часть файла не загрузилась после ${MAX_RETRIES} попыток. Проверь интернет и попробуй снова. ${
        lastError instanceof Error ? lastError.message : ''
      }`,
    );
  }

  /** Отправка одной части с отслеживанием каждого килобайта */
  private sendPart(url: string, blob: Blob, partNumber: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.requests.add(xhr);

      const finish = () => this.requests.delete(xhr);

      xhr.open('PUT', url, true);

      // Вот ради этого и нужен XMLHttpRequest: событие приходит много раз
      // по мере отправки, а не один раз в конце
      xhr.upload.onprogress = (e) => {
        this.inflight.set(partNumber, e.loaded);
        this.emit('uploading');
      };

      xhr.onload = () => {
        finish();
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag');
          if (!etag) {
            reject(new Error('Хранилище не вернуло идентификатор части'));
            return;
          }
          resolve(etag.replace(/"/g, ''));
        } else {
          reject(new Error(`Хранилище ответило ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        finish();
        reject(new Error('Обрыв соединения'));
      };
      xhr.ontimeout = () => {
        finish();
        reject(new Error('Превышено время ожидания'));
      };
      xhr.onabort = () => {
        finish();
        reject(new Error('Загрузка отменена'));
      };

      xhr.send(blob);
    });
  }

  /**
   * Сообщить о прогрессе.
   *
   * События отправки прилетают десятки раз в секунду. Перерисовывать
   * интерфейс на каждое — расточительно и на глаз незаметно, поэтому
   * ограничиваем частоту. Важные моменты пропускаем без задержки.
   */
  private emit(status: UploadProgress['status'], error?: string, immediate = false) {
    const now = Date.now();
    if (!immediate && now - this.lastEmit < EMIT_INTERVAL_MS) return;
    this.lastEmit = now;

    const total = this.file.size;
    let inflightBytes = 0;
    for (const bytes of this.inflight.values()) inflightBytes += bytes;

    const uploaded = Math.min(total, this.completedBytes + inflightBytes);

    // Копим замеры и выбрасываем всё старше окна
    this.samples.push({ at: now, bytes: uploaded });
    const cutoff = now - SPEED_WINDOW_MS;
    while (this.samples.length > 2 && this.samples[0]!.at < cutoff) this.samples.shift();

    const oldest = this.samples[0]!;
    const seconds = (now - oldest.at) / 1000;
    const speed =
      seconds > 0.5 ? (uploaded - oldest.bytes) / seconds / 1024 ** 2 : 0;

    const remaining = total - uploaded;
    const etaSec = speed > 0.1 ? Math.ceil(remaining / (speed * 1024 ** 2)) : null;

    this.onProgress({
      percent: total ? (uploaded / total) * 100 : 0,
      uploadedBytes: uploaded,
      totalBytes: total,
      speedMbps: Number(speed.toFixed(1)),
      etaSec,
      status,
      error,
    });
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
